import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { MdModeEdit, MdRestoreFromTrash } from "react-icons/md";
import Header from "../../header/header";
import { CustomSpinner } from "../../shared/Spinner";
import DeleteModal from "../../shared/DeleteModal";
import { db } from "../../../firebase";
import {
  EXTRA_RELATION_OPTIONS,
  OTHER_RELATION_VALUE,
  filterFamilyDropdownPlaceholders,
  mergeRelationFromForm,
  splitRelationForForm,
} from "../../../utils/keyContactRelations";
import {
  KeyContactPhoneField,
  phoneFingerprintForDirectoryId,
} from "../../shared/KeyContactPhoneField";
import styles from "./KeyPlayerContactsDirectory.module.scss";

const CONTACT_DIRECTORY_COLLECTION = "key-player-contacts-directory";
const PLAYER_KEY_CONTACTS_COLLECTION = "player-key-contacts";
const FAMILY_DROPDOWN_COLLECTION = "family-dropdown";

const EMPTY_FORM = {
  relationSelect: "",
  relationOther: "",
  fullName: "",
  cell: "",
  email: "",
  notes: "",
};

const normalizeText = (value) => String(value || "").trim();

/** directory doc id -> sorted unique player names from player-key-contacts */
async function fetchReferenceByDirectoryId() {
  const snap = await db.collection(PLAYER_KEY_CONTACTS_COLLECTION).get();
  const map = new Map();
  snap.forEach((doc) => {
    const d = doc.data();
    const dirId = d.directoryContactId;
    if (!dirId) return;
    const name = normalizeText(d.playerName) || normalizeText(d.playerProfile) || "";
    if (!name) return;
    if (!map.has(dirId)) map.set(dirId, new Set());
    map.get(dirId).add(name);
  });
  const out = {};
  map.forEach((set, dirId) => {
    out[dirId] = Array.from(set).sort((a, b) => a.localeCompare(b));
  });
  return out;
}

const makeDirectoryDocId = ({ fullName, cell, email }) => {
  const raw = `${normalizeText(fullName).toLowerCase()}|${phoneFingerprintForDirectoryId(cell)}|${normalizeText(email).toLowerCase()}`;
  const cleaned = raw.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
  return cleaned || `contact_${Date.now()}`;
};

export default function KeyPlayerContactsDirectory() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [referenceByDirectoryId, setReferenceByDirectoryId] = useState({});

  const loadDirectory = async () => {
    setLoading(true);
    try {
      const [snap, refMap] = await Promise.all([
        db.collection(CONTACT_DIRECTORY_COLLECTION).get(),
        fetchReferenceByDirectoryId(),
      ]);
      const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      rows.sort((a, b) => normalizeText(a.fullName).localeCompare(normalizeText(b.fullName)));
      setItems(rows);
      setReferenceByDirectoryId(refMap);
    } catch (error) {
      console.error("Error loading contact directory:", error);
      toast.error("Could not load contacts directory");
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const snap = await db.collection(FAMILY_DROPDOWN_COLLECTION).orderBy("order").get();
      setFamilyMembers(snap.docs.map((doc) => doc.data().value).filter(Boolean));
    } catch (error) {
      console.error("Error loading family dropdown:", error);
    }
  };

  useEffect(() => {
    loadDirectory();
    loadFamilyMembers();
  }, []);

  const filteredItems = useMemo(() => {
    const term = normalizeText(search).toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const refStr = (referenceByDirectoryId[item.id] || []).join(", ").toLowerCase();
      return (
        normalizeText(item.fullName).toLowerCase().includes(term) ||
        normalizeText(item.cell).toLowerCase().includes(term) ||
        normalizeText(item.email).toLowerCase().includes(term) ||
        normalizeText(item.relation).toLowerCase().includes(term) ||
        refStr.includes(term)
      );
    });
  }, [items, search, referenceByDirectoryId]);

  const familyMembersForRelation = useMemo(
    () => filterFamilyDropdownPlaceholders(familyMembers),
    [familyMembers]
  );

  const extraRelationOptionsFiltered = useMemo(
    () => EXTRA_RELATION_OPTIONS.filter((opt) => !familyMembersForRelation.includes(opt)),
    [familyMembersForRelation]
  );

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
  };

  const openAddModal = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      ...splitRelationForForm(item.relation, familyMembersForRelation),
      fullName: item.fullName || "",
      cell: item.cell || "",
      email: item.email || "",
      notes: item.notes || "",
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "relationSelect") {
      setForm((prev) => ({
        ...prev,
        relationSelect: value,
        relationOther: value === OTHER_RELATION_VALUE ? prev.relationOther : "",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const fullName = normalizeText(form.fullName);
    if (!fullName) {
      toast.error("Full Name is required");
      return;
    }

    const relation = mergeRelationFromForm(form.relationSelect, form.relationOther);
    if (form.relationSelect === OTHER_RELATION_VALUE && !normalizeText(form.relationOther)) {
      toast.error("Enter how this contact relates to the player (or pick a preset above)");
      return;
    }

    const payload = {
      relation,
      fullName,
      fullNameLower: fullName.toLowerCase(),
      cell: normalizeText(form.cell),
      email: normalizeText(form.email),
      notes: normalizeText(form.notes),
      updatedAt: new Date(),
    };

    try {
      if (editingItem?.id) {
        await db.collection(CONTACT_DIRECTORY_COLLECTION).doc(editingItem.id).set(payload, { merge: true });
        toast.success("Contact updated");
      } else {
        const newId = makeDirectoryDocId(payload);
        await db.collection(CONTACT_DIRECTORY_COLLECTION).doc(newId).set({
          ...payload,
          createdAt: new Date(),
        });
        toast.success("Contact added");
      }
      closeModal();
      await loadDirectory();
    } catch (error) {
      console.error("Error saving contact directory item:", error);
      toast.error("Could not save contact");
    }
  };

  const handleDelete = async (id) => {
    try {
      await db.collection(CONTACT_DIRECTORY_COLLECTION).doc(id).delete();
      toast.success("Contact deleted");
      await loadDirectory();
    } catch (error) {
      console.error("Error deleting contact directory item:", error);
      toast.error("Could not delete contact");
    }
  };

  if (loading) return <CustomSpinner />;

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.content}>
        <div className={styles.title}>
          <h4>Key Contacts Directory</h4>
          <p>
            View and manage the full contacts database used for key player contact suggestions.
          </p>
        </div>
        <hr />

        <div className={styles.controls}>
          <Form.Control
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by full name, phone, or email"
            className={styles.searchInput}
          />
          <div className={styles.buttons}>
            <button type="button" className={styles.primaryBtn} onClick={openAddModal}>
              Add Contact
            </button>
          </div>
        </div>

        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <span>Total</span>
            <span>{filteredItems.length}</span>
          </div>
          <div className={`${styles.row} ${styles.headerRow}`}>
            <div>Player Reference</div>
            <div>Contact</div>
            <div>Full Name</div>
            <div>Cell</div>
            <div>Email</div>
            <div>Notes</div>
            <div>Actions</div>
          </div>
          {filteredItems.length === 0 ? (
            <div className={styles.empty}>No contacts found.</div>
          ) : (
            filteredItems.map((item) => (
              <div key={item.id} className={styles.row}>
                <div className={styles.referenceCell}>
                  {(referenceByDirectoryId[item.id] || []).length > 0
                    ? (referenceByDirectoryId[item.id] || []).join(", ")
                    : "--"}
                </div>
                <div>{item.relation || "--"}</div>
                <div>{item.fullName || "--"}</div>
                <div>{item.cell || "--"}</div>
                <div>{item.email || "--"}</div>
                <div className={styles.notesCell}>{item.notes || "--"}</div>
                <div className={styles.actions}>
                  <MdModeEdit onClick={() => openEditModal(item)} />
                  <MdRestoreFromTrash onClick={() => setSelectedDeleteId(item.id)} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingItem ? "Edit Contact" : "Add Contact"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Contact</Form.Label>
              <Form.Select name="relationSelect" value={form.relationSelect} onChange={handleChange}>
                <option value="">Select Member</option>
                {familyMembersForRelation.map((member) => (
                  <option key={member} value={member}>
                    {member}
                  </option>
                ))}
                {extraRelationOptionsFiltered.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                <option value={OTHER_RELATION_VALUE}>Other</option>
              </Form.Select>
              {form.relationSelect === OTHER_RELATION_VALUE && (
                <Form.Control
                  className="mt-2"
                  type="text"
                  name="relationOther"
                  value={form.relationOther}
                  onChange={handleChange}
                  placeholder="Describe relationship to the player"
                />
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                required
                placeholder="e.g. John Doe"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cell</Form.Label>
              <KeyContactPhoneField
                value={form.cell}
                onChange={(formatted) => setForm((prev) => ({ ...prev, cell: formatted }))}
                inputProps={{ id: "directory-contact-cell", "aria-label": "Cell phone" }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="e.g. contact@email.com"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Add context or communication notes"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <DeleteModal
        show={Boolean(selectedDeleteId)}
        handleClose={() => setSelectedDeleteId(null)}
        handleConfirm={() => handleDelete(selectedDeleteId)}
        title="Delete Contact"
        label="Are you sure you want to delete this contact from the suggestions directory?"
      />
    </div>
  );
}
